import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

const GALLERY_VIDEO_SELECTOR =
  ':is(.product-media-container--video, .product-media-container--external_video) deferred-media';

/**
 * @typedef {HTMLElement & {
 *   showDeferredMedia?: (focus?: boolean) => void
 *   playMedia?: () => void
 *   pauseMedia?: () => void
 * }} GalleryVideoMedia
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    this.#setupVideoAutoplay();
  }

  #controller = new AbortController();

  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#videoObserver?.disconnect();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Plays the video in the selected slideshow slide, and pauses the rest.
   */
  #handleSlideshowSelect = () => {
    this.#syncVideoPlayback();
  };

  /**
   * Autoplay gallery videos when they are scrolled into view.
   */
  #setupVideoAutoplay() {
    const videos = this.#galleryVideos;
    if (!videos.length) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement)) continue;

          this.#setVideoPlaying(entry.target, entry.isIntersecting && this.#shouldAutoplay(entry.target));
        }
      },
      { threshold: 0.5 }
    );

    for (const media of videos) {
      this.#videoObserver.observe(media);
    }

    this.#syncVideoPlayback();
  }

  /**
   * Play or pause each gallery video based on whether it is the current, visible media.
   */
  #syncVideoPlayback() {
    for (const media of this.#galleryVideos) {
      this.#setVideoPlaying(media, this.#shouldAutoplay(media));
    }
  }

  /**
   * @param {HTMLElement} media
   * @returns {boolean}
   */
  #shouldAutoplay(media) {
    if (prefersReducedMotion()) return false;
    if (!media.getClientRects().length) return false;

    const slide = media.closest('slideshow-slide');
    if (!slide) return true;

    const currentSlide = this.slideshow?.slides?.[this.slideshow.current];
    return currentSlide === slide;
  }

  /**
   * @param {HTMLElement} media
   * @param {boolean} shouldPlay
   */
  #setVideoPlaying(media, shouldPlay) {
    const videoMedia = /** @type {GalleryVideoMedia} */ (media);

    if (shouldPlay) {
      videoMedia.showDeferredMedia?.(false);
      return;
    }

    videoMedia.pauseMedia?.();
  }

  /** @returns {NodeListOf<HTMLElement>} */
  get #galleryVideos() {
    return this.querySelectorAll(GALLERY_VIDEO_SELECTOR);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
